import {connect} from 'react-redux';

import {ErrorWithData} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  CellsPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {
  makeGetIsInTeamContext,
  makeGetTags,
  makeGetTopics,
} from '../../../../../store/data/conversations/conversationsSelectors';
import {
  subscribeToConversationsAction,
  unsubscribeFromConversationsAction,
} from '../../../../../store/data/conversations/subscribeConversationsActions';
import {updateConversationSubjectAction} from '../../../../../store/data/conversations/updateConversationSubjectAction';
import {
  getFindTeammate,
  getIsUserVisitor,
  getUserId,
} from '../../../../../store/data/teammates/teammatesSelectors';
import {InboxesVisualConversationSelection} from '../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {
  makeGetKnownSelectedConversations,
  makeGetOnlySelectedConversation,
} from '../../../../../store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ConversationHeader} from './conversationHeader';

/*
 * Props.
 */

interface ConversationHeaderContainerProps {
  panelId: ViewerPanelId;
  cellsPanelId: CellsPanelId | undefined;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: unknown, props: ConversationHeaderContainerProps) {
  return props.cellsPanelId;
}

function getCellsContext(_state: unknown, props: ConversationHeaderContainerProps) {
  return props.cellsContext;
}

function getConversationSelection(_state: unknown, props: ConversationHeaderContainerProps) {
  return props.conversationSelection;
}

function makeGetTeammate() {
  return createSelector([getCellsContext, getFindTeammate], (cellsContext, findTeammate) => {
    const teammate = findTeammate(cellsContext.teammateId);
    if (!teammate) {
      throw new ErrorWithData('A teammate could not be found for the current context.', {
        teammateId: cellsContext.teammateId,
      });
    }

    return teammate;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationHeaderContainerProps>()(() => {
  const getTeammate = makeGetTeammate();
  const getSelectedConversations = makeGetKnownSelectedConversations(
    getConversationSelection,
    getCellsPanelId,
  );
  const getIsInTeamContext = makeGetIsInTeamContext(getCellsContext, getSelectedConversations);

  return {
    userId: getUserId,
    teammate: getTeammate,
    conversations: getSelectedConversations,
    topics: makeGetTopics(getSelectedConversations),
    tags: wrapInShallowSelector(makeGetTags(getTeammate, getSelectedConversations, getIsInTeamContext)),
    onlyConversation: makeGetOnlySelectedConversation(getConversationSelection),
    isUserVisitor: getIsUserVisitor,
    isInTeamContext: getIsInTeamContext,
    company: getCompany,
  };
});

export const ConversationHeaderContainer = connect(
  mapStateToProps,
  {
    subscribeToConversationsAction,
    unsubscribeFromConversationsAction,
    updateConversationSubjectAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const {conversationSelection} = ownProps;

    // TODO: Push this down into `ConversationHeaderSubject`.
    const onSubjectChange = (subject: string) => {
      if (!conversationSelection.isFullyKnown) {
        return;
      }

      const conversationIds = conversationSelection.knownIds;
      dispatchProps.updateConversationSubjectAction(conversationIds, subject);
    };

    return {
      ...stateProps,
      ...ownProps,
      onSubjectChange,
    };
  },
)(ConversationHeader);
